/**
 * BWOF color rules – applied consistently across the app.
 *
 * 1) Building name: due-period color, OR green if council application approved.
 * 2) Status (Pending/Processing/Completed etc.): color follows SS/building annual remaining period (red/amber/gray).
 * 3) SS code: due-period color, OR green if SS inspection passed, OR purple if any cycle in period missed.
 *
 * Due-period: past due = deep red (overdue); 0–30d left = red (urgent); 31–60d = amber; else gray.
 * Overdue and urgent are mutually exclusive.
 */

/**
 * Due-period text class only (used for status and as base for SS/building when no override).
 * @param {number | null | undefined} daysUntilDue
 * @returns {string} Tailwind text class
 */
export function getSsDueTextClassFromDays(daysUntilDue) {
  if (daysUntilDue == null || daysUntilDue > 60) return 'text-gray-900';
  if (daysUntilDue < 0) return 'text-red-900'; // overdue (past due)
  if (daysUntilDue <= 30) return 'text-red-700'; // urgent (still due ≤30d)
  return 'text-amber-700'; // upcoming 31–60d
}

/**
 * Due-period badge class (bg + text) for status – status follows SS/building annual remaining period.
 * @param {number | null | undefined} daysUntilDue
 * @returns {string} Tailwind classes for badge
 */
export function getStatusBadgeClassByDue(daysUntilDue) {
  if (daysUntilDue == null || daysUntilDue > 60) return 'bg-gray-100 text-gray-800';
  if (daysUntilDue < 0) return 'bg-red-800 text-white'; // overdue
  if (daysUntilDue <= 30) return 'bg-red-100 text-red-800'; // urgent window
  return 'bg-amber-100 text-amber-800';
}

/**
 * Building name: green if council application approved, else due-period color.
 * @param {number | null | undefined} daysUntilDue
 * @param {boolean} [applicationApproved]
 * @returns {string} Tailwind text class
 */
export function getBuildingNameClass(daysUntilDue, applicationApproved) {
  if (applicationApproved === true) return 'text-green-700 font-semibold';
  return getSsDueTextClassFromDays(daysUntilDue);
}

/**
 * SS code/text: green if passed, purple if cycle missed, else due-period color.
 * @param {number | null | undefined} daysUntilDue
 * @param {boolean} [passed] - SS inspection passed
 * @param {boolean} [missed] - any cycle in period omitted
 * @returns {string} Tailwind text class
 */
export function getSsDisplayClass(daysUntilDue, passed, missed) {
  if (passed === true) return 'text-green-700 font-bold';
  if (missed === true) return 'text-purple-700 font-bold';
  return getSsDueTextClassFromDays(daysUntilDue);
}

/** @deprecated Use getSsDisplayClass or getSsDueTextClassFromDays. Kept for compatibility. */
export function getSsDueTextClass({ inWindow, isUrgent } = {}) {
  if (!inWindow) return 'text-gray-900';
  if (isUrgent) return 'text-red-700';
  return 'text-amber-700';
}

/**
 * Same rule for card/border background (e.g. Order page SS cards).
 * @param {number | null | undefined} daysUntilDue
 * @returns {string} Tailwind classes for border + bg
 */
export function getSsDueCardClass(daysUntilDue) {
  if (daysUntilDue == null || daysUntilDue > 60) return 'border-gray-300 bg-gray-50';
  if (daysUntilDue < 0) return 'border-red-700 bg-red-100'; // overdue
  if (daysUntilDue <= 30) return 'border-red-400 bg-red-50'; // urgent
  return 'border-amber-400 bg-amber-50';
}
